import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, Loader2, MessageSquare } from 'lucide-react';
import { dtpApi } from '../../lib/dtpApi';
import { useDtpAuthStore } from '../../store/useDtpAuthStore';
import { NotificationCategory, NotificationChannel, NotificationSettings, selfServiceError } from '../../lib/patientSelfService';

const CATEGORIES: { key: NotificationCategory; label: string; hint: string }[] = [
  { key: 'orderUpdates', label: 'Order updates', hint: 'Shipping and tracking for your orders' },
  { key: 'labResults', label: 'Lab results', hint: 'When new results are ready to view' },
  { key: 'careMessages', label: 'Care messages', hint: 'Messages from your care team' },
  { key: 'refillReminders', label: 'Refill reminders', hint: 'When it’s time to reorder' },
];
const CHANNELS: { key: NotificationChannel; label: string }[] = [{ key: 'sms', label: 'Text' }, { key: 'email', label: 'Email' }];

/**
 * What the patient hears about, and how (Account page). Each switch saves on
 * its own; a failed save puts the switch back and says so.
 */
export function NotificationPreferences() {
  const patientId = useDtpAuthStore(s => s.patient?._id);
  const impersonated = useDtpAuthStore(s => s.impersonated);
  const queryClient = useQueryClient();
  const key = ['patient-notifications', patientId];
  const settings = useQuery({ queryKey: key, retry: false, gcTime: 0, queryFn: () => dtpApi.getNotificationPreferences() });
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  async function apply(id: string, change: () => Promise<NotificationSettings>, optimistic?: (current: NotificationSettings) => NotificationSettings) {
    const previous = settings.data;
    setSaving(id); setError(''); setNotice('');
    if (previous && optimistic) queryClient.setQueryData(key, optimistic(previous));
    try {
      queryClient.setQueryData(key, await change());
      setNotice('Saved.');
    } catch (err) {
      if (previous) queryClient.setQueryData(key, previous);
      setError(selfServiceError(err, 'We couldn’t save that change. Please try again.'));
    } finally { setSaving(null); }
  }

  const toggle = (category: NotificationCategory, channel: NotificationChannel, on: boolean) =>
    apply(`${category}.${channel}`, () => dtpApi.updateNotificationPreferences({ [category]: { [channel]: on } }),
      current => ({ ...current, prefs: { ...current.prefs, [category]: { ...current.prefs[category], [channel]: on } } }));

  const data = settings.data;
  const sms = data?.sms;
  const textsBlocked = Boolean(sms && (!sms.canText || sms.optedOut));
  const readOnly = Boolean(impersonated);

  return <section aria-labelledby="notification-preferences" className="portal-card mt-5 p-6 sm:p-8">
    <div className="flex items-center gap-3">
      <span aria-hidden="true" className="grid h-9 w-9 place-items-center rounded-xl bg-[var(--portal-tint-2)]"><Bell className="h-[18px] w-[18px]" /></span>
      <h2 id="notification-preferences" className="portal-display text-xl font-semibold">Notifications</h2>
    </div>
    <p className="mt-2 text-sm text-[var(--portal-muted)]">Choose what we tell you about, and how. Sign-in codes, sign-in links, payment links and receipts always reach you; these settings don’t affect them.</p>
    {readOnly && <p className="mt-3 rounded-lg bg-white/70 p-3 text-sm">Only the patient can change notification settings.</p>}

    {settings.isPending ? <p role="status" className="mt-5 text-sm text-[var(--portal-muted)]">Loading…</p>
      : settings.isError || !data ? <div role="alert" className="mt-5 text-sm">We couldn’t load your notification settings. <button className="min-h-[44px] font-semibold underline" onClick={() => settings.refetch()}>Try again</button></div>
      : <>
        <div className="mt-5 overflow-hidden rounded-2xl border border-[var(--portal-line)] bg-white/70">
          <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 border-b border-[var(--portal-line)] px-4 py-2 text-xs font-semibold uppercase tracking-[0.08em] text-[var(--portal-muted)] sm:gap-x-8">
            <span>Notify me about</span>{CHANNELS.map(c => <span key={c.key} className="w-12 text-center">{c.label}</span>)}
          </div>
          <ul className="divide-y divide-[var(--portal-line)]">
            {CATEGORIES.map(category => <li key={category.key} className="grid grid-cols-[1fr_auto_auto] items-center gap-x-4 px-4 py-3 sm:gap-x-8">
              <span className="min-w-0"><span className="block text-[15px] font-medium">{category.label}</span><span className="block text-xs text-[var(--portal-muted)]">{category.hint}</span></span>
              {CHANNELS.map(channel => {
                const id = `${category.key}.${channel.key}`;
                const unavailable = channel.key === 'sms' ? textsBlocked : !data.hasEmail;
                return <span key={channel.key} className="flex w-12 justify-center">
                  <Switch label={`${category.label} by ${channel.label.toLowerCase()}`} checked={data.prefs[category.key][channel.key] && !unavailable}
                    disabled={readOnly || unavailable || saving !== null} busy={saving === id}
                    onChange={on => toggle(category.key, channel.key, on)} />
                </span>;
              })}
            </li>)}
          </ul>
        </div>
        {!data.hasEmail && <p className="mt-3 text-sm text-[var(--portal-muted)]">Add an email address above to get notifications by email.</p>}

        {sms && <TextsPanel sms={sms} readOnly={readOnly} busy={saving === 'sms'} disabled={saving !== null}
          onChange={optedOut => apply('sms', () => dtpApi.setTextsOptedOut(optedOut))} />}

        <div aria-live="polite" className="mt-3 min-h-[1.25rem] text-sm">
          {error ? <p role="alert" className="rounded-lg bg-red-50 p-3 text-red-700">{error}</p> : notice ? <p className="text-green-700">{notice}</p> : null}
        </div>
      </>}
  </section>;
}

function TextsPanel({ sms, readOnly, busy, disabled, onChange }: { sms: NotificationSettings['sms']; readOnly: boolean; busy: boolean; disabled: boolean; onChange: (optedOut: boolean) => void }) {
  const phone = sms.phoneLast4 ? `your number ending in ${sms.phoneLast4}` : 'your number';
  if (!sms.canText) return <p className="mt-4 text-sm text-[var(--portal-muted)]">We don’t have a number we can text on your account. Contact your clinic to update it.</p>;
  return <div className="mt-5 rounded-2xl bg-[var(--portal-tint-1)] p-4 text-sm">
    <p className="flex items-center gap-2 font-semibold"><MessageSquare aria-hidden="true" className="h-4 w-4" />{sms.optedOut ? 'Texts are off' : 'Texts'}</p>
    {!sms.optedOut ? <>
      <p className="mt-1 text-[var(--portal-muted)]">Turn off every text to {phone} except sign-in codes. You can also reply STOP to any text from us.</p>
      {!readOnly && <PillButton busy={busy} disabled={disabled} onClick={() => onChange(true)}>Stop all texts</PillButton>}
    </> : sms.optedOutBy === 'stop' ? (
      <p className="mt-1 text-[var(--portal-muted)]">You replied STOP, so we don’t text {phone} (sign-in codes still arrive). To get texts again, reply START to any text from us.</p>
    ) : sms.optedOutBy === 'web' ? <>
      <p className="mt-1 text-[var(--portal-muted)]">You turned off texts to {phone}. Sign-in codes still arrive.</p>
      {!readOnly && <PillButton busy={busy} disabled={disabled} onClick={() => onChange(false)}>Turn texts back on</PillButton>}
    </> : (
      <p className="mt-1 text-[var(--portal-muted)]">Texts to {phone} were turned off by your clinic. Contact them to turn texts back on.</p>
    )}
  </div>;
}

function PillButton({ busy, disabled, onClick, children }: { busy: boolean; disabled: boolean; onClick: () => void; children: string }) {
  return <button type="button" onClick={onClick} disabled={disabled}
    className="mt-3 inline-flex h-10 items-center gap-2 rounded-full bg-white/90 px-4 text-sm font-semibold ring-1 ring-[var(--portal-line)] transition hover:bg-white disabled:opacity-50">
    {busy && <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" />}{children}
  </button>;
}

function Switch({ label, checked, disabled, busy, onChange }: { label: string; checked: boolean; disabled: boolean; busy: boolean; onChange: (on: boolean) => void }) {
  return <button type="button" role="switch" aria-checked={checked} aria-label={label} aria-busy={busy || undefined} disabled={disabled} onClick={() => onChange(!checked)}
    className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full transition focus:outline-none focus-visible:ring-4 focus-visible:ring-[rgba(224,86,58,0.25)] disabled:cursor-not-allowed ${checked ? 'bg-[var(--portal-accent)]' : 'bg-[rgba(36,26,21,0.16)]'} ${disabled && !busy ? 'opacity-50' : ''}`}>
    <span aria-hidden="true" className={`grid h-5 w-5 place-items-center rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`}>
      {busy && <Loader2 className="h-3 w-3 animate-spin text-[var(--portal-muted)]" />}
    </span>
  </button>;
}
